import { useEffect, useState } from "react";
import Reveal from "../common/Reveal";
import ShopByCategory from "./ShopByCategory";
import CategoryRail from "./CategoryRail";
import {
  TbDeviceMobile, TbHome, TbPerfume, TbSeedling, TbSun,
  TbBallFootball, TbMoodKid, TbCar, TbBooks, TbTool,
} from "react-icons/tb";
import CartRecoveryBanner from "../cart/CartRecoveryBanner";
import ProductRail from "../product/ProductRail";
import HeroSlideshow from "./HeroSlideshow";
import { API_BASE as API, cachedFetch } from "../../api/api";
import { getSessionId } from "../../utils/analytics";
import { useSEO } from "../../utils/seo";
import { META_DESCRIPTION, organizationSchema, websiteSchema } from "../../config/site";
import DiscoveryPanel from "./DiscoveryPanel";
import Newsletter from "./Newsletter";
import "./Home.css";

// Cosmetic-only per-rail display tweaks (eyebrow copy, the dark first-rail
// theme, where "View all" links to) — never eligibility/data, which is
// entirely backend-driven (GET /rails + GET /rails/{slug}/home). A rail
// with no entry here just gets sensible defaults below.
const RAIL_DISPLAY = {
  new_arrivals:    { eyebrow: "Just In",               dark: true, viewAllTo: "/new-arrivals" },
  best_sellers:    { eyebrow: "Customer Favourites",    viewAllTo: "/suits" },
  trending:        { eyebrow: "Trending Now" },
  staff_picks:     { eyebrow: "Staff Picks" },
  pre_order:       { eyebrow: "Coming Soon" },
  almost_gone:     { eyebrow: "Limited Stock" },
  back_in_stock:   { eyebrow: "Restocked" },
  limited_edition: { eyebrow: "Limited Edition" },
  clearance:       { eyebrow: "Clearance" },
  flash_sale:      { eyebrow: "Flash Sale" },
  bundles:         { eyebrow: "Frequently Bought Together" },
};

// Built from src/config/site.js rather than restated here — this block used
// to carry its own copy of the old category-specific brand description, which is how
// that wording reached the homepage's structured data as well as its meta
// tags. The static fallback in public/index.html is the same graph.
const HOME_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [organizationSchema(), websiteSchema()],
};

const TRUST = [
  { icon: "🛡️", label: "100% Authentic",    detail: "Every item verified" },
  { icon: "🚚", label: "Nationwide Delivery",  detail: "Rates from Ksh 200" },
  { icon: "↩️",  label: "14-Day Returns",    detail: "Hassle-free policy" },
  { icon: "📱", label: "Pay via M-Pesa",     detail: "Fast & secure" },
];

// Outline icons in the label's grey (CategoryRail.css), standing in for the
// emoji this rail used to show: 📱 🏠 💄 🌱 ☀️ ⚽ 👶 🚗 📚 🔧. Tabler has no
// lipstick, so Beauty uses its perfume bottle.
const PLATFORM_CATS = [
  { icon: <TbDeviceMobile />, label: "Electronics",   to: "/category/phones-tablets" },
  { icon: <TbHome />,         label: "Home",          to: "/category/home-living" },
  { icon: <TbPerfume />,      label: "Beauty",        to: "/category/beauty-health" },
  { icon: <TbSeedling />,     label: "Agriculture",   to: "/category/agriculture" },
  { icon: <TbSun />,          label: "Solar",         to: "/category/solar-energy" },
  { icon: <TbBallFootball />, label: "Sports",        to: "/category/sports-outdoors" },
  { icon: <TbMoodKid />,      label: "Baby & Kids",   to: "/category/baby-kids" },
  { icon: <TbCar />,          label: "Vehicles",      to: "/category/vehicles" },
  { icon: <TbBooks />,        label: "Books",         to: "/category/books-stationery" },
  { icon: <TbTool />,         label: "Industrial",    to: "/category/industrial" },
];

// Homepage background: the site-wide dot-wave layer (SiteBackground.jsx,
// "full" here). The rail panels have no background of their own, so the
// pattern runs on unbroken behind them (owner's choice, version B). See
// "Site-wide dot-wave background" in CLAUDE.md.

const Home = ({ onAddToCart, cart = [] }) => {
  useSEO({
    // The homepage is the one page whose title is NOT "{page} | Emporia" —
    // it is the approved title verbatim, so useSEO is given no `title` and
    // falls back to the configured home title.
    description: META_DESCRIPTION,
    path: "/",
    schema: HOME_SCHEMA,
  });

  // Rails are entirely backend-driven (RailConfig, see backend/app/rails.py)
  // — fetched once here, rendered dynamically below. No rail is hardcoded.
  const [rails, setRails] = useState([]);
  useEffect(() => {
    cachedFetch(`${API}/rails`)
      .then((data) => setRails(Array.isArray(data) ? data : []))
      .catch(() => setRails([]));
  }, []);

  const sessionId = getSessionId();
  const renderRail = (rail, i, dark) => {
    const meta = RAIL_DISPLAY[rail.slug] || {};
    return (
      <ProductRail
        key={rail.slug}
        eyebrow={meta.eyebrow || rail.display_name}
        title={rail.display_name}
        endpoint={`/rails/${rail.slug}/home?session_id=${encodeURIComponent(sessionId)}`}
        viewAllTo={meta.viewAllTo || `/rails/${rail.slug}`}
        cols={6}
        dark={dark ?? !!meta.dark}
        lazy={i > 0}
        onAddToCart={onAddToCart}
        railKey={rail.slug}
      />
    );
  };

  // First rail renders above the editorial section (the original "New
  // Arrivals" slot), the rest below — a positional layout choice, not a
  // hardcoded rail slug; display_order (server-side) decides what's first.
  const [firstRail, ...restRails] = rails;

  return (
    <main className="home home--dots">
      <h1 className="home-seo-heading">Emporia — Kenya's Online Marketplace</h1>

      <CartRecoveryBanner cart={cart} onAddToCart={onAddToCart} />

      {/* ══ 1. HERO ══════════════════════════════════════════════ */}
      <HeroSlideshow />

      {/* ══ 2. TRUST STRIP ═══════════════════════════════════════ */}
      <Reveal as="div" className="trust-strip reveal-children">
        {TRUST.map((t, i) => (
          <div key={t.label} className="trust-item" style={{ "--i": i }}>
            <div>
              <p className="trust-label">{t.label}</p>
              <p className="trust-detail">{t.detail}</p>
            </div>
          </div>
        ))}
      </Reveal>

      {/* ══ 3. SHOP BY CATEGORY ══════════════════════════════════ */}
      <section className="section">
        <div className="section-header">
          <div>
            <p className="section-eye">Collections</p>
            <h2 className="section-title">Shop by Category</h2>
          </div>
        </div>
        <ShopByCategory />
      </section>

      {/* ══ 4. FIRST RAIL ════════════════════════════════════════ */}
      {firstRail && renderRail(firstRail, 0)}

      {/* ══ 5. DISCOVERY PANEL ═══════════════════════════════════ */}
      <Reveal as="div" className="reveal">
        <DiscoveryPanel onAddToCart={onAddToCart} />
      </Reveal>

      {/* ══ 6–16. REMAINING RAILS ════════════════════════════════ */}
      {/* Alternate backgrounds so the rails don't blur into one long
          scroll — ends on `dark=false` (Bundles) so it doesn't sit flush
          against "More on the Platform" below, which is also alt-toned. */}
      {restRails.map((rail, i) => renderRail(rail, i + 1, i % 2 === 0))}

      {/* ══ 16. PLATFORM CATEGORIES ══════════════════════════════ */}
      <section className="section section-alt">
        <div className="section-header">
          <div>
            <p className="section-eye">Everything, delivered</p>
            <h2 className="section-title">More on the Platform</h2>
          </div>
        </div>
        <Reveal as="div" className="reveal category-strip">
          <CategoryRail categories={PLATFORM_CATS} />
        </Reveal>
      </section>

      {/* ══ 17. NEWSLETTER ═══════════════════════════════════════ */}
      <Newsletter />

    </main>
  );
};

export default Home;
